import pdfIcon from "@/core/services/Files/extensionIcons/pdf.png";
import pngIcon from "@/core/services/Files/extensionIcons/png.png";
import jpgIcon from "@/core/services/Files/extensionIcons/jpg.png";
import xlsIcon from "@/core/services/Files/extensionIcons/xls.png";
import xlsxIcon from "@/core/services/Files/extensionIcons/xlsx.png";
import docIcon from "@/core/services/Files/extensionIcons/doc.png";
import zipIcon from "@/core/services/Files/extensionIcons/zip.png";
import odtIcon from "@/core/services/Files/extensionIcons/odt.png";
import anyIcon from "@/core/services/Files/extensionIcons/page.png";

import { unref as vueUnref } from 'vue';
import Core from "@/core/Core";

/**
 * Утилиты для работы с файлами
 * Общие методы для конвертации, скачивания и обработки файлов
 */
class FileUtils {
	/**
	 * Карта MIME типов
	 * @private
	 */
	private static readonly MIME_TYPES: Record<string, string> = {
		'pdf': 'application/pdf',
		'jpg': 'image/jpeg',
		'jpeg': 'image/jpeg',
		'png': 'image/png',
		'gif': 'image/gif',
		'webp': 'image/webp',
		'svg': 'image/svg+xml',
		'bmp': 'image/bmp',
		'doc': 'application/msword',
		'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		'xls': 'application/vnd.ms-excel',
		'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		'zip': 'application/zip',
		'rar': 'application/x-rar-compressed',
		'7z': 'application/x-7z-compressed',
		'txt': 'text/plain',
		'csv': 'text/csv',
		'rtf': 'application/rtf',
		'odt': 'application/vnd.oasis.opendocument.text',
		'ods': 'application/vnd.oasis.opendocument.spreadsheet'
	};

	/**
	 * Единицы измерения размера файлов
	 * @private
	 */
	private static readonly SIZE_UNITS = ['Б', 'КБ', 'МБ', 'ГБ'] as const;

	/**
	 * Конвертация файла в base64
	 * @param file - Файл для конвертации
	 * @returns Base64 строка без префикса
	 */
	static fileToBase64(file: File | Blob): Promise<string> {
		file = vueUnref(file);

		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				// Убираем префикс data:mime;base64,
				const base64 = (reader.result as string).split(',')[1];
				resolve(base64);
			};
			reader.onerror = error => reject(error);
		});
	}

	static fileToDataURL(file: File | Blob): Promise<string> {
		file = vueUnref(file);

		return new Promise((resolve, reject) => {
			// Проверки перед чтением
			if (!file) {
				reject(new Error('Файл не предоставлен'));
				return;
			}

			if (!(file instanceof File || file instanceof Blob)) {
				reject(new Error('Передан не File/Blob объект'));
				return;
			}
			const reader = new FileReader();

			reader.onload = () => resolve(reader.result as string);

			reader.onerror = (error) => {
				Core.error('FileReader error: ', error);
				Core.error('Error type: ', reader.error);

				reject(reader.error);
			};

			reader.readAsDataURL(file);
		});
	}

	/**
	 * Конвертация base64 в Blob
	 * @param b64Data - Base64 данные
	 * @param contentType - MIME тип
	 * @param name - Имя файла (опционально)
	 * @returns Blob объект или null
	 */
	static base64ToBlob(b64Data: string, contentType: string = '', name?: string): Blob | null {
		b64Data = vueUnref(b64Data);

		if (!b64Data) return null;

		const byteCharacters = atob(b64Data);
		const bytesLength = byteCharacters.length;
		const sliceSize = 512;
		const slicesCount = Math.ceil(bytesLength / sliceSize);
		const byteArrays = new Array(slicesCount);

		for (let sliceIndex = 0; sliceIndex < slicesCount; sliceIndex += 1) {
			const begin = sliceIndex * sliceSize;
			const end = Math.min(begin + sliceSize, bytesLength);

			const bytes = new Array(end - begin);
			for (let offset = begin, i = 0; offset < end; i += 1, offset += 1) {
				bytes[i] = byteCharacters[offset].charCodeAt(0);
			}
			byteArrays[sliceIndex] = new Uint8Array(bytes);
		}

		const blob = new Blob(byteArrays, { type: contentType });
		if (name) {
			(blob as any).name = name;
		}
		return blob;
	}

	/**
	 * Скачать файл из base64 данных
	 * @param base64Data - Base64 данные
	 * @param fileName - Имя файла
	 * @param mimeType - MIME тип
	 */
	static downloadFromBase64(base64Data: string, fileName: string, mimeType: string = ''): void {
		const blob = this.base64ToBlob(base64Data, mimeType, fileName);
		if (blob) {
			this.downloadBlob(blob);
		}
	}

	/**
	 * Скачать Blob как файл
	 * @param blob - Blob для скачивания
	 */
	static downloadBlob(blob: Blob | null): void {
		blob = vueUnref(blob);

		if (!blob) return;

		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.style.display = 'none';
		a.href = url;

		if ((blob as any).name) {
			a.download = (blob as any).name;
		}

		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	/**
	 * Получить MIME тип по расширению файла
	 * @param fileName - Имя файла или расширение
	 * @returns MIME тип
	 */
	static getMimeType(fileName: string): string {
		fileName = vueUnref(fileName);

		// Получаем расширение (или используем переданную строку как расширение)
		const extension = fileName.includes('.') ?
			fileName.split('.').pop()!.toLowerCase() :
			fileName.toLowerCase();

		return this.MIME_TYPES[extension] || 'application/octet-stream';
	}

	/**
	 * Форматирование размера в байтах
	 * @param bytes - Размер в байтах
	 * @returns Отформатированный размер
	 */
	static formatSize(bytes: number): string {
		bytes = vueUnref(bytes);

		if (bytes === 0) return '0 Б';

		const k = 1024;
		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + this.SIZE_UNITS[i];
	}

	static ICONS_MAP: Record<string, any> = {
		pdf: pdfIcon,
		png: pngIcon,
		jpg: jpgIcon,
		jpeg: jpgIcon,
		xls: xlsIcon,
		xlsx: xlsxIcon,
		doc: docIcon,
		docx: docIcon,
		zip: zipIcon,
		rar: zipIcon,
		'7z': zipIcon,
		odt: odtIcon,
		any: anyIcon,
	};
}

export default FileUtils;
